import { z } from 'zod';
/**
 * Web-Analytics (B3, order 27-9) — the wire shape of `GET /web-analytics/uebersicht`.
 *
 * Normbron: `docs/ANALYTICS-INTERN-EXTERN-ONTWERP.md` §1 (G1), §4 and the owner
 * decisions E2 (a), E3a, E6 and E13; the operating notes are in
 * `docs/WEB-ANALYTICS.md`.
 *
 * ── WHAT CROSSES THE WIRE ─────────────────────────────────────────────────────
 * Only AGGREGATES that the backend read out of its own nightly snapshot table.
 * The CRM never talks to Google, and neither does the request that serves this
 * shape: the snapshots are written by a cron, the endpoint only reads them.
 *
 * ── G1: RAW IN THE INTERNAL SNAPSHOT; ONE PROTECTED TABLE PER DAY ─────────────
 * (master, nazorg 2 after RV629b). The table holds the raw day values and nobody but
 * the backend reads it. Each day of each Bereich is protected ONCE, by itself, into
 * a fixed day table (A: a count 1…4 is hidden, 0 stays 0; a hidden day total hides
 * that metric for the day; B/C: pairs and ratios need ≥ 5 on both sides; D: a list
 * that partitions a released day total hides more until no hidden value can be
 * pinned down). Every answer — whatever window — is built from those tables only:
 *
 *   - a DAY (`proTag`) shows its released cells ({@link WebAnalyticsZelle});
 *   - a PERIOD is the SUM of the released day cells ({@link WebAnalyticsSumme}):
 *     `untergrenze: true` as soon as one day of that metric (or row) is hidden —
 *     the UI shows «≥ n». With little traffic (finavest.ch) day and period values
 *     are mostly lower bounds;
 *   - a period RATIO is weighted over the days that released it
 *     ({@link WebAnalyticsQuote}, `tage: { beruecksichtigt, gesamt }`).
 *
 * So two windows differ only in released values, and no hidden day value ever
 * enters a sum. A period counts USER-DAYS: the sum of the daily users is named
 * `nutzerTageSumme` (overview and sources); only a day carries `totalUsers`.
 */
/** The four sources of external figures. `verwaltung` is NOT a tenant (§2.1). */
export const WEB_ANALYTICS_BEREICHE = ['holding', 'mlg', 'finavest', 'verwaltung'];
export const webAnalyticsBereichSchema = z.enum(WEB_ANALYTICS_BEREICHE);
/** The five fixed reports of one day and one Bereich (one Data-API request each). */
export const WEB_ANALYTICS_RAPPORTE = [
    'uebersicht',
    'seiten',
    'quellen',
    'ereignisse',
    'schluesselereignisse',
];
export const webAnalyticsRapportSchema = z.enum(WEB_ANALYTICS_RAPPORTE);
/** G1: a count 1…k−1 of a day is never released (k = 5). */
export const WEB_ANALYTICS_K_SCHWELLE = 5;
/** The period when the request names none: the last 30 days up to yesterday. */
export const WEB_ANALYTICS_ZEITRAUM_STANDARD_TAGE = 30;
/** The longest period one request may ask for; a longer one is clamped. */
export const WEB_ANALYTICS_ZEITRAUM_MAX_TAGE = 366;
/**
 * How many rows each list SHOWS (order B3): the backend cuts to it, and the CRM's
 * Extern tab shows at most that many (B3-UI) — one number, read by both sides. The
 * key events are not cut.
 */
export const WEB_ANALYTICS_ANZEIGE_LIMITS = Object.freeze({
    seiten: 20,
    quellen: 20,
    ereignisse: 30,
});
/**
 * A calendar date `YYYY-MM-DD` that EXISTS — `2026-02-30` is refused, not
 * rolled over into March by `Date`.
 */
export const webAnalyticsDatumSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD')
    .refine((s) => {
    const [j, m, t] = s.split('-').map(Number);
    const d = new Date(Date.UTC(j, m - 1, t));
    return d.getUTCFullYear() === j && d.getUTCMonth() === m - 1 && d.getUTCDate() === t;
}, 'no such calendar date');
/**
 * `?von=YYYY-MM-DD&bis=YYYY-MM-DD&bereich=` — all optional; the defaults are the service's.
 *
 * `bereich` (B5, order 29-9): the view the caller ASKS for. Whether it may is the
 * server's decision alone (`auswahl` of the answer): a Bereich outside it is a 403
 * `bereich_nicht_waehlbar`, never a silent fallback to the own Bereich. Without it the
 * server delivers the Bereich of the active tenant, as before.
 */
export const webAnalyticsQuerySchema = z
    .object({
    von: webAnalyticsDatumSchema.optional(),
    bis: webAnalyticsDatumSchema.optional(),
    bereich: webAnalyticsBereichSchema.optional(),
})
    .refine((q) => q.von === undefined || q.bis === undefined || q.von <= q.bis, {
    message: 'von liegt nach bis',
    path: ['von'],
});
/** G1 rule D: the suppressed mass of a day's partition reaches at least 2k. */
export const WEB_ANALYTICS_MINDEST_UNTERDRUECKT = 2 * WEB_ANALYTICS_K_SCHWELLE;
/**
 * ONE DAY's cell: `wert: n` = exactly n (0 included) · `wert: null, unterdrueckt:
 * true` = hidden, the UI shows «–» · `wert: null, unterdrueckt: false` = no data.
 */
export const webAnalyticsZelleSchema = z.object({
    wert: z.number().nullable(),
    unterdrueckt: z.boolean(),
});
/**
 * A PERIOD's count = the sum of the released day cells. `untergrenze: true` = at least
 * one day of it is hidden, the sum is a lower bound («≥ n»). Nothing released but
 * hidden days = `wert: null, unterdrueckt: true` («–»). No day with data = `wert:
 * null, unterdrueckt: false`.
 */
export const webAnalyticsSummeSchema = z.object({
    wert: z.number().nullable(),
    unterdrueckt: z.boolean(),
    untergrenze: z.boolean(),
});
/**
 * A PERIOD's ratio, weighted over the days that released it: `tage.beruecksichtigt`
 * of the `tage.gesamt` days with an overview. None released = «–».
 */
export const webAnalyticsQuoteSchema = z.object({
    wert: z.number().nullable(),
    unterdrueckt: z.boolean(),
    tage: z.object({
        beruecksichtigt: z.number().int().nonnegative(),
        gesamt: z.number().int().nonnegative(),
    }),
});
/** The PERIOD: sums of released day cells; users summed over the days are user-days. */
export const webAnalyticsKennzahlenSchema = z.object({
    sessions: webAnalyticsSummeSchema,
    nutzerTageSumme: webAnalyticsSummeSchema,
    newUsers: webAnalyticsSummeSchema,
    screenPageViews: webAnalyticsSummeSchema,
    engagementRate: webAnalyticsQuoteSchema,
    averageSessionDuration: webAnalyticsQuoteSchema,
});
/** ONE day, as its table released it: here the users are GA4's `totalUsers` of that day. */
export const webAnalyticsTagSchema = z.object({
    datum: webAnalyticsDatumSchema,
    sessions: webAnalyticsZelleSchema,
    totalUsers: webAnalyticsZelleSchema,
    newUsers: webAnalyticsZelleSchema,
    screenPageViews: webAnalyticsZelleSchema,
    engagementRate: webAnalyticsZelleSchema,
    averageSessionDuration: webAnalyticsZelleSchema,
});
export const webAnalyticsSeiteSchema = z.object({
    pagePath: z.string(),
    screenPageViews: webAnalyticsSummeSchema,
    sessions: webAnalyticsSummeSchema,
});
export const webAnalyticsQuelleSchema = z.object({
    sessionSource: z.string(),
    sessionMedium: z.string(),
    sessionCampaignName: z.string(),
    sessions: webAnalyticsSummeSchema,
    /** Users of this source summed over the days of the period — user-days. */
    nutzerTageSumme: webAnalyticsSummeSchema,
});
export const webAnalyticsEreignisSchema = z.object({
    eventName: z.string(),
    eventCount: webAnalyticsSummeSchema,
});
/** Why there is nothing to show — two closed reasons, nothing else. */
export const WEB_ANALYTICS_GRUENDE = ['kein_bereich', 'kein_zugang'];
export const webAnalyticsNichtKonfiguriertSchema = z.object({
    konfiguriert: z.literal(false),
    grund: z.enum(WEB_ANALYTICS_GRUENDE),
});
export const webAnalyticsUebersichtSchema = z.object({
    konfiguriert: z.literal(true),
    bereich: webAnalyticsBereichSchema,
    /**
     * The GA4 property id of the Bereich (B3-UI), from the backend's static table
     * `bereichQuellen(env)`: holding, mlg and verwaltung carry `GA4_PROPERTY_MLG`,
     * finavest `GA4_PROPERTY_FINAVEST`. Only for the «In GA4 öffnen» link — the CRM
     * never calls Google with it.
     */
    property: z.string(),
    zeitraum: z.object({
        von: webAnalyticsDatumSchema,
        bis: webAnalyticsDatumSchema,
        tage: z.number().int().positive(),
    }),
    /** The newest `abgerufenAm` of the snapshots in the period; null = no snapshot yet. */
    stand: z.string().nullable(),
    /** The period: sums of the released day cells (users as user-days). */
    uebersicht: webAnalyticsKennzahlenSchema,
    proTag: z.array(webAnalyticsTagSchema),
    seiten: z.array(webAnalyticsSeiteSchema),
    quellen: z.array(webAnalyticsQuelleSchema),
    ereignisse: z.array(webAnalyticsEreignisSchema),
    schluesselereignisse: z.array(webAnalyticsEreignisSchema),
    hinweise: z.object({
        /** GA4 itself held data back in at least one snapshot (`subjectToThresholding`). */
        thresholding: z.boolean(),
        /** GA4 rolled rows into «(other)» in at least one snapshot (`dataLossFromOtherRow`). */
        dataLoss: z.boolean(),
    }),
    /**
     * B5 (order 29-9): the Bereiche the CALLER may pick (`?bereich=`), in the server's
     * order — the holding `['holding', 'mlg', 'verwaltung']` (gesamt first; finavest.ch
     * stays out, E6), every other tenant only its own. The server decides it; the CRM
     * shows a choice only when there is more than one and never derives it from the
     * tenant. `bereich` above is the DELIVERED one.
     */
    auswahl: z.array(webAnalyticsBereichSchema).min(1),
});
export const webAnalyticsAntwortSchema = z.discriminatedUnion('konfiguriert', [
    webAnalyticsNichtKonfiguriertSchema,
    webAnalyticsUebersichtSchema,
]);
